import { memo } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { remarkWhatsappEmphasis, remarkDisableAttention } from "../lib/remark-whatsapp-emphasis";
import { safeUrl } from "../lib/safeUrl";
import { CodeBlock } from "./CodeBlock";
import styles from "./ChatView.module.css";

interface Props {
  text: string;
}

const REMARK_PLUGINS = [remarkDisableAttention, remarkGfm, remarkWhatsappEmphasis];

export const MessageMarkdown = memo(function MessageMarkdown({ text }: Props) {
  return (
    <ReactMarkdown
      remarkPlugins={REMARK_PLUGINS}
      urlTransform={safeUrl}
      skipHtml
      components={{
        a: ({ href, children }) => (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer nofollow"
            className={styles.messageLink}
          >
            {children}
          </a>
        ),
        img: () => null,
        code: ({ className, children, node }) => {
          const langMatch = /language-([a-zA-Z0-9_+\-]+)/.exec(className ?? "");
          // Block detection: language-X class present (fenced with lang),
          // OR node spans multiple source lines (multi-line fenced even without lang).
          const startLine = node?.position?.start?.line;
          const endLine = node?.position?.end?.line;
          const multiLine = startLine != null && endLine != null && startLine !== endLine;
          const isBlock = !!langMatch || multiLine;
          if (isBlock) {
            const code = String(children).replace(/\n$/, "");
            return <CodeBlock code={code} lang={langMatch ? langMatch[1]! : ""} />;
          }
          return <code className={styles.inlineCode}>{children}</code>;
        },
        pre: ({ children }) => <>{children}</>,
        h1: ({ children }) => <h3 className={styles.mdHeader}>{children}</h3>,
        h2: ({ children }) => <h3 className={styles.mdHeader}>{children}</h3>,
        h3: ({ children }) => <h3 className={styles.mdHeader}>{children}</h3>,
        h4: ({ children }) => <h3 className={styles.mdHeader}>{children}</h3>,
        h5: ({ children }) => <h3 className={styles.mdHeader}>{children}</h3>,
        h6: ({ children }) => <h3 className={styles.mdHeader}>{children}</h3>,
        ul: ({ children }) => <ul className={styles.mdList}>{children}</ul>,
        ol: ({ children }) => <ol className={styles.mdList}>{children}</ol>,
        // KOL-58: horizontal-scroll wrapper keeps multi-column tables (Diana's
        // Stufen-Analysen) readable in narrow bubbles instead of squeezing or
        // overflowing the bubble on mobile.
        table: ({ children }) => (
          <div className={styles.mdTableWrap}>
            <table className={styles.mdTable}>{children}</table>
          </div>
        ),
      }}
    >
      {text}
    </ReactMarkdown>
  );
});
